<template>
  <div>
    <div class="bg-white rounded-lg shadow-sm mb-6">
      <button
        type="button"
        class="w-full flex items-center justify-between px-6 py-4 border-b border-gray-200 hover:bg-gray-50 transition"
        @click="showFilters = !showFilters"
      >
        <span class="text-sm font-semibold text-gray-800">Filtros</span>
        <svg
          class="w-5 h-5 text-gray-500 transform transition-transform"
          :class="showFilters ? 'rotate-180' : ''"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      <div v-if="showFilters" class="px-6 py-4">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-3 items-end">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Imóvel</label>
            <PropertySelectAsync
              v-model="filters.propertyId"
              placeholder="Todos os imóveis"
              :required="false"
            />
          </div>
          <div>
            <PersonSelectAsync
              v-model="filters.guestId"
              role="customer"
              label="Hóspede"
              placeholder="Todos os hóspedes"
              :allow-create="false"
              :show-whats-app-link="false"
            />
          </div>
          <div>
            <PersonSelectAsync
              v-model="filters.ownerId"
              role="owner"
              label="Proprietário do imóvel"
              placeholder="Todos os proprietários"
              :allow-create="false"
              :show-whats-app-link="false"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Responsável do imóvel</label>
            <SelectWithFilter
              v-model="filters.responsibleId"
              :options="responsibleUserOptions"
              placeholder="Todos os responsáveis"
            />
          </div>
          <div>
            <PersonSelectAsync
              v-model="filters.sellerId"
              role="agent"
              label="Corretor"
              placeholder="Todos os corretores"
              :allow-create="false"
              :show-whats-app-link="false"
            />
          </div>
          <div class="min-w-0 w-full">
            <div class="flex w-full min-w-0 gap-2 items-end">
              <div class="min-w-0 flex-1">
                <label class="block text-xs font-medium text-gray-600 mb-0.5">Data inicial check-in</label>
                <input
                  v-model="filters.startDate"
                  type="date"
                  class="w-full min-w-0 text-sm py-1.5 px-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div class="min-w-0 flex-1">
                <label class="block text-xs font-medium text-gray-600 mb-0.5">Data final check-in</label>
                <input
                  v-model="filters.endDate"
                  type="date"
                  class="w-full min-w-0 text-sm py-1.5 px-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
          </div>
          <div class="md:col-start-3 flex justify-end">
            <button
              type="button"
              class="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition flex items-center justify-center space-x-2"
              @click="applyFilters"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <span>Buscar</span>
            </button>
          </div>
        </div>
        <div v-if="dateError" class="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {{ dateError }}
        </div>
      </div>
    </div>

    <div v-if="loading" class="flex justify-center items-center h-64">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
    </div>

    <div v-else class="bg-white rounded-lg shadow-sm overflow-hidden">
      <div class="sm:hidden">
        <div v-if="rows.length === 0" class="px-6 py-12 text-center text-gray-500">
          Nenhuma reserva encontrada
        </div>
        <div v-else class="divide-y divide-gray-200">
          <div
            v-for="row in rows"
            :key="row.id"
            class="p-4 bg-white"
          >
            <div class="flex items-start justify-between gap-3">
              <div class="min-w-0">
                <p class="text-sm font-semibold text-gray-900 truncate" :title="row.property_name">
                  {{ row.property_name || '-' }}
                </p>
                <p class="text-xs text-gray-500 mt-1">{{ row.guest_name || '-' }}</p>
              </div>
              <div class="flex items-center gap-2 flex-shrink-0">
                <button
                  type="button"
                  class="text-indigo-600 hover:text-indigo-900 p-1"
                  title="Editar"
                  @click="$emit('edit-stay', { id: row.id, type: 'Stay' })"
                >
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
              </div>
            </div>
            <div class="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
              <div class="text-gray-500">Check-in</div>
              <div class="text-gray-900 font-medium">{{ formatDate(row.check_in_date) }}</div>
              <div class="text-gray-500">Check-out</div>
              <div class="text-gray-900 font-medium">{{ formatDate(row.check_out_date) }}</div>
              <div class="text-gray-500">Responsável</div>
              <div class="text-gray-900 font-medium truncate">{{ row.responsible_name || '-' }}</div>
              <div class="text-gray-500">Valor total</div>
              <div class="text-indigo-700 font-semibold text-right tabular-nums">
                R$ {{ formatCurrencyDisplay(row.total_value) }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="hidden sm:block overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-200">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Imóvel</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Hóspede</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Check-in</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Check-out</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Responsável</th>
              <th class="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">Valor total</th>
              <th class="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">Ações</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-if="rows.length === 0">
              <td colspan="7" class="px-6 py-12 text-center text-gray-500">
                Nenhuma reserva encontrada
              </td>
            </tr>
            <template v-else>
              <tr
                v-for="row in rows"
                :key="row.id"
                class="hover:bg-gray-50"
              >
                <td class="px-6 py-4 text-sm text-gray-900 max-w-xs truncate" :title="row.property_name">{{ row.property_name || '-' }}</td>
                <td class="px-6 py-4 text-sm text-gray-900 max-w-xs truncate" :title="row.guest_name">{{ row.guest_name || '-' }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{{ formatDate(row.check_in_date) }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{{ formatDate(row.check_out_date) }}</td>
                <td class="px-6 py-4 text-sm text-gray-600 max-w-xs truncate" :title="row.responsible_name">{{ row.responsible_name || '-' }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm font-semibold text-indigo-700 text-right tabular-nums">
                  R$ {{ formatCurrencyDisplay(row.total_value) }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                  <button
                    type="button"
                    class="text-indigo-600 hover:text-indigo-900 inline-flex p-1"
                    title="Editar"
                    @click="$emit('edit-stay', { id: row.id, type: 'Stay' })"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <div v-if="pagination.count > 0" class="px-6 py-4 border-t border-gray-200 bg-white">
        <div v-if="pagination.pages <= 1" class="flex items-center justify-center">
          <p class="text-sm text-gray-700">
            Total de
            <span class="font-medium">{{ pagination.count }}</span>
            {{ pagination.count === 1 ? 'registro' : 'registros' }}
          </p>
        </div>
        <div v-else class="flex items-center justify-between">
          <div class="flex-1 flex justify-between sm:hidden">
            <button
              type="button"
              class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="!pagination.prev"
              @click="changePage(pagination.page - 1)"
            >
              Anterior
            </button>
            <button
              type="button"
              class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="!pagination.next"
              @click="changePage(pagination.page + 1)"
            >
              Próxima
            </button>
          </div>
          <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div class="flex items-center space-x-4">
              <p class="text-sm text-gray-700">
                Mostrando
                <span class="font-medium">{{ pagination.from }}</span>
                a
                <span class="font-medium">{{ pagination.to }}</span>
                de
                <span class="font-medium">{{ pagination.count }}</span>
                resultados
              </p>
            </div>
            <div>
              <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  type="button"
                  class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  :disabled="!pagination.prev"
                  @click="changePage(pagination.page - 1)"
                >
                  <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clip-rule="evenodd" />
                  </svg>
                </button>
                <template v-for="page in visiblePages" :key="page">
                  <button
                    v-if="page !== '...'"
                    type="button"
                    :class="[
                      'relative inline-flex items-center px-4 py-2 border text-sm font-medium',
                      page === pagination.page
                        ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                    ]"
                    @click="changePage(page)"
                  >
                    {{ page }}
                  </button>
                  <span
                    v-else
                    class="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                  >
                    ...
                  </span>
                </template>
                <button
                  type="button"
                  class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  :disabled="!pagination.next"
                  @click="changePage(pagination.page + 1)"
                >
                  <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import SelectWithFilter from './SelectWithFilter.vue'
import PersonSelectAsync from './PersonSelectAsync.vue'
import PropertySelectAsync from './PropertySelectAsync.vue'

function defaultMonthDateRange() {
  const today = new Date()
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1)
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0)
  return {
    startDate: firstDay.toISOString().split('T')[0],
    endDate: lastDay.toISOString().split('T')[0]
  }
}

export default {
  name: 'BookingHistory',

  components: {
    SelectWithFilter,
    PersonSelectAsync,
    PropertySelectAsync
  },

  emits: ['edit-stay'],

  data() {
    const { startDate, endDate } = defaultMonthDateRange()
    return {
      showFilters: true,
      rows: [],
      loading: false,
      dateError: '',
      responsibleUsers: [],
      filters: {
        propertyId: '',
        guestId: '',
        ownerId: '',
        responsibleId: '',
        sellerId: '',
        startDate,
        endDate
      },
      pagination: {
        page: 1,
        items: 20,
        count: 0,
        pages: 0,
        from: 0,
        to: 0,
        prev: null,
        next: null
      }
    }
  },

  computed: {
    responsibleUserOptions() {
      return [
        { value: '', label: 'Todos os responsáveis' },
        ...(Array.isArray(this.responsibleUsers) ? this.responsibleUsers.map(u => ({
          value: String(u.id),
          label: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email
        })) : [])
      ]
    },

    visiblePages() {
      const current = this.pagination.page
      const total = this.pagination.pages
      const delta = 2
      const range = []
      const rangeWithDots = []

      for (let i = Math.max(2, current - delta); i <= Math.min(total - 1, current + delta); i++) {
        range.push(i)
      }

      if (current - delta > 2) {
        rangeWithDots.push(1, '...')
      } else {
        rangeWithDots.push(1)
      }

      rangeWithDots.push(...range)

      if (current + delta < total - 1) {
        rangeWithDots.push('...', total)
      } else if (total > 1) {
        rangeWithDots.push(total)
      }

      return rangeWithDots
    }
  },

  mounted() {
    this.bootstrap()
  },

  methods: {
    async bootstrap() {
      await this.loadResponsibleUsers()
      this.applyFilters()
    },

    /** Chamado pelo pai após salvar/excluir reserva no modal. */
    refresh() {
      if (this.validateDateRange()) {
        this.loadRows()
      }
    },

    formatDate(dateString) {
      if (!dateString) return '-'
      const [year, month, day] = dateString.split('-')
      const date = new Date(year, month - 1, day)
      return date.toLocaleDateString('pt-BR')
    },

    formatCurrencyDisplay(value) {
      const numValue = typeof value === 'string' ? parseFloat(value.replace(',', '.')) : parseFloat(value || 0)
      return numValue.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },

    async loadResponsibleUsers() {
      if (Array.isArray(this.responsibleUsers) && this.responsibleUsers.length > 0) return
      try {
        const userToken = localStorage.getItem('userToken')
        const tenantToken = localStorage.getItem('tenantToken')
        const response = await axios.get('/api/v1/users', {
          headers: {
            Authorization: `Bearer ${userToken}`,
            'Tenant-Authorization': `Bearer ${tenantToken}`
          }
        })
        this.responsibleUsers = response.data?.data || response.data || []
      } catch (error) {
        console.error('Erro ao carregar usuários para filtro de responsável:', error)
        this.responsibleUsers = []
      }
    },

    validateDateRange() {
      const start = this.filters.startDate
      const end = this.filters.endDate

      if (!start || !end) {
        this.dateError = 'Informe data inicial e data final para pesquisar.'
        return false
      }

      const startDate = new Date(start)
      const endDate = new Date(end)

      if (isNaN(startDate) || isNaN(endDate)) {
        this.dateError = 'Datas inválidas.'
        return false
      }

      if (endDate < startDate) {
        this.dateError = 'A data final não pode ser menor que a data inicial.'
        return false
      }

      const maxEndDate = new Date(startDate)
      maxEndDate.setFullYear(maxEndDate.getFullYear() + 1)

      if (endDate > maxEndDate) {
        this.dateError = 'O período máximo é de 1 ano.'
        return false
      }

      this.dateError = ''
      return true
    },

    applyFilters() {
      this.pagination.page = 1
      if (!this.validateDateRange()) return
      this.loadRows()
    },

    async loadRows() {
      if (!this.validateDateRange()) {
        return
      }

      this.loading = true
      try {
        const userToken = localStorage.getItem('userToken')
        const tenantToken = localStorage.getItem('tenantToken')

        const params = {
          list_mode: 'booking_history',
          page: this.pagination.page
        }
        if (this.filters.propertyId) params.property_id = this.filters.propertyId
        if (this.filters.guestId) params.customer_id = this.filters.guestId
        if (this.filters.ownerId) params.owner_id = this.filters.ownerId
        if (this.filters.responsibleId) params.responsible_id = this.filters.responsibleId
        if (this.filters.sellerId) params.seller_id = this.filters.sellerId
        if (this.filters.startDate) params.start_date = this.filters.startDate
        if (this.filters.endDate) params.end_date = this.filters.endDate

        const queryString = new URLSearchParams(params).toString()
        const response = await axios.get(`/api/v1/stays?${queryString}`, {
          headers: {
            Authorization: `Bearer ${userToken}`,
            'Tenant-Authorization': `Bearer ${tenantToken}`
          }
        })

        if (response.data?.data && response.data?.pagy) {
          this.rows = Array.isArray(response.data.data) ? response.data.data : []
          this.pagination = response.data.pagy
        } else {
          this.rows = []
        }
      } catch (err) {
        console.error('Erro ao carregar histórico de reservas:', err)
        this.rows = []
      } finally {
        this.loading = false
      }
    },

    changePage(page) {
      if (page === '...' || page < 1) return
      this.pagination.page = page
      this.loadRows()
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }
}
</script>
